import { useContext, useEffect, useMemo } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InstanceContext } from '@/contexts/InstanceContext';
import { useStars } from '@/contexts/StarContext';
import {
  EmbeddedCompanion,
  useCompanionMoment,
  useNamedCompanionSpeech,
} from '@/components/character';
import { useChildTokens } from '@/components/child';
import ActivityEvaluation from '@/components/activity/viewActivity/ActivityEvaluation';
import { useActivePersona } from '@/personas/useActivePersona';

/**
 * Celebration — barnvyns firande efter avklarad aktivitet (prototypens DONE).
 * Stjärnregn (starBurst-animation), centrerad firande-kompanjon (celebrate-mood),
 * utvärdering (ActivityEvaluation → /evaluation; barnet får tre bilder, tonåringen
 * samma steg som vuxna) och en väg
 * tillbaka till start.
 *
 * Vid mount uppdateras stjärnsaldot (StarContext.refresh) eftersom backend
 * delar ut en stjärna när aktiviteten markeras klar (complete-hooken). Tills
 * stjärn-backenden svarar ger refresh nollvärden utan att krascha.
 */

// Slumpade positioner för stjärnregnet (stabila per mount).
const STAR_POSITIONS = [
  { left: '12%', delay: 0 },
  { left: '28%', delay: 0.12 },
  { left: '46%', delay: 0.05 },
  { left: '64%', delay: 0.18 },
  { left: '82%', delay: 0.1 },
  { left: '20%', delay: 0.22 },
  { left: '72%', delay: 0.28 },
];

const Celebration = () => {
  const { t } = useTranslation();
  const tokens = useChildTokens();
  const navigate = useNavigate();
  const { instance } = useContext(InstanceContext);
  const { refresh } = useStars();
  // Barnvyn visas även för tonåringen. Barnet får den förenklade utvärderingen med tre
  // bilder, tonåringen 5-skalan med energi och taggar.
  const { persona } = useActivePersona();

  // Firandet tilltalar barnet vid namn när ett giltigt förnamn finns.
  const { speechKey, speechVars } = useNamedCompanionSpeech('child.companion.celebrate');
  useCompanionMoment('celebrate', speechKey, speechVars);

  // Uppdatera stjärnsaldot — backend delade ut en stjärna vid complete.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const stars = useMemo(() => STAR_POSITIONS, []);

  return (
    <Container sx={{ pb: 4, position: 'relative', overflow: 'hidden' }}>
      {/* Stjärnregn */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        {stars.map((s, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              top: -24,
              left: s.left,
              fontSize: '1.5rem',
              animation: `starRain 1.4s ease-in ${s.delay}s both`,
              '@keyframes starRain': {
                '0%': { transform: 'translateY(0) rotate(0deg)', opacity: 0 },
                '20%': { opacity: 1 },
                '100%': { transform: 'translateY(70vh) rotate(180deg)', opacity: 0 },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', display: 'none' },
            }}
          >
            ⭐
          </Box>
        ))}
      </Box>

      <Stack
        spacing={2.5}
        alignItems="center"
        textAlign="center"
        pt={3}
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <EmbeddedCompanion variant="celebrate" accent="primary" />

        <Typography sx={{ fontSize: '2rem', fontWeight: 900, color: 'primary.main' }}>
          {t('child.celebration.title')} 🎉
        </Typography>

        <Box
          sx={{
            fontSize: '3rem',
            animation: 'starPop 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
            '@keyframes starPop': {
              from: { transform: 'scale(0) rotate(-20deg)', opacity: 0 },
              to: { transform: 'scale(1) rotate(0deg)', opacity: 1 },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          ⭐
        </Box>

        {/* Utvärderingen — samma komponent som vuxnas dialog, i barnets eller tonåringens variant. */}
        {instance?.activityInstanceId !== undefined && (
          <Box
            sx={{
              width: '100%',
              maxWidth: 340,
              bgcolor: tokens.card,
              border: '1px solid',
              borderColor: tokens.border,
              borderRadius: '16px',
              p: 2,
              boxShadow: tokens.shadow,
            }}
          >
            <Typography sx={{ fontWeight: 800, mb: 1, color: tokens.text }}>
              {t('child.celebration.howFelt')}
            </Typography>
            <ActivityEvaluation variant={persona === 'teen' ? 'standard' : 'child'} />
          </Box>
        )}

        <Box
          component="button"
          type="button"
          onClick={() => navigate('/')}
          sx={{
            p: '18px 36px',
            borderRadius: '18px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            border: 'none',
            fontWeight: 800,
            fontSize: '1.1rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          {t('child.celebration.backToStart')} →
        </Box>
      </Stack>
    </Container>
  );
};

export default Celebration;
